import { DEMO_DOMAINS } from '../constant';
import { toHHmmss } from './time-utils';

/**
 * Checks if a username is a standard @weave-users.com format (without + tag)
 * @returns true if standard format (first.last@weave-users.com), false if has tag (first.last+prod@weave-users.com)
 */
const isStandardWeaveUsersDomain = (username: string): boolean => {
  if (!username.endsWith(DEMO_DOMAINS.WEAVE_USERS)) {
    return false;
  }

  const localPart = username.substring(0, username.indexOf(DEMO_DOMAINS.WEAVE_USERS));
  // If local part contains +, it has a tag like +prod, +dev, +etc (non-demo)
  return !localPart.includes('+');
};

/**
 * Checks if username matches any legacy demo domain
 */
const isLegacyDemoDomain = (username: string): boolean => {
  return (
    username.includes(DEMO_DOMAINS.PATTERSON_DENTAL) ||
    username.includes(DEMO_DOMAINS.PATTERSON_COMPANIES) ||
    username.includes(DEMO_DOMAINS.DEMO_WEAVE)
  );
};

/**
 * Checks if a user is a demo user based on their email domain
 * @returns true if the username is a demo account:
 *  - Standard format: first.last@weave-users.com
 *  - Legacy domains: @pattersondental.com, @pattersoncompanies.com, @demoweave.com
 *  - Excludes engineer testing accounts with + tags: first.last+prod@weave-users.com, first.last+dev@weave-users.com, etc.
 */
export const isDemoUser = (username?: string): boolean => {
  if (!username) return false;

  const lowercaseUsername = username.toLowerCase();

  return isStandardWeaveUsersDomain(lowercaseUsername) || isLegacyDemoDomain(lowercaseUsername);
};

/**
 * To get the enum key from the value
 */
export const getEnumKeyByValue = <T extends Record<string, number | string | undefined>>(
  enumObject: T,
  value?: number | string
): string => {
  if (value) {
    for (const key in enumObject) {
      if (enumObject[key] === value || enumObject[key] === parseInt(value as string)) {
        return key;
      }
    }
  }
  return '';
};

export const parseTranscript = (transcript: string, tokens?: Record<string, string | undefined>) => {
  const regex = /\[(.*?)\] <(.*?) --> (.*?)> (.+?)\n/g;
  const result = [];
  let match;

  while ((match = regex.exec(transcript)) !== null) {
    const time = {
      start: toHHmmss(parseFloat(match[2] || '0'), true),
      end: toHHmmss(parseFloat(match[3] || '0'), true),
    };
    const speaker = match[1];
    const text = match[4];

    result.push({ time, speaker, text: !tokens ? text : parseTranscriptWithTokens(text ?? '', tokens) });
  }

  return result;
};

export const parseTranscriptWithTokens = (transcript: string, tokens: Record<string, string | undefined>): string => {
  if (!transcript) {
    return '';
  }
  return transcript.replace(/\[([^\]]+)\]/g, (match, p1) => tokens[p1] || match);
};

export const findCitationInTranscript = ({
  citation,
  transcript,
}: {
  citation: string;
  transcript: string;
}): { start: number; end: number; isFound: boolean } => {
  const startIndex = transcript.toLowerCase().indexOf(citation.toLowerCase());
  const endIndex = startIndex === -1 ? -1 : startIndex + citation.length;
  return { start: startIndex, end: endIndex, isFound: startIndex !== -1 };
};
